import React, { useState } from 'react';
import { fn } from '../api/functions';
import { useNavigate } from 'react-router-dom';

export default function DemoLandingPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Basic validation
      if (!name.trim() || !email.trim()) {
        setError('Please enter your name and email');
        setLoading(false);
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Call backend function
      const result = await fn.createDemoUser({
        name: name.trim(),
        email: email.trim().toLowerCase()
      });

      if (result.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      console.error('Error creating demo user:', err);
      if (err.code === 'already-exists') {
        setError('You already have an active demo session. Check your email for the link!');
      } else if (err.code === 'resource-exhausted') {
        setError('Too many demo requests. Please try again tomorrow.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          <h2 className="font-serif text-2xl font-semibold text-gray-900 mb-4">
            Check Your Email!
          </h2>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            We've sent a confirmation link to <strong>{email}</strong>. Click the link in the email to start your demo recording.
          </p>
          
          <p className="text-sm text-gray-500">
            Can't find it? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#061D3D] mb-4">
            Try Memory Stream
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Experience how easy it is to preserve your life stories
          </p>
          <p className="text-base text-gray-500">
            Record a quick answer and see your words transcribed instantly
          </p>
        </div>

        {/* Demo Form Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87400] focus:border-[#C87400] text-base"
                disabled={loading}
                required
              />
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87400] focus:border-[#C87400] text-base"
                disabled={loading}
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C87400] hover:bg-[#B06600] text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Try Recording Demo'
              )}
            </button>

            {/* Privacy Notice */}
            <p className="text-xs text-gray-500 text-center">
              By continuing, you agree to receive emails from Memory Stream. You can unsubscribe anytime.
            </p>
          </form>
        </div>

        {/* What to Expect */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">What happens next?</h3>
          <ol className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="font-semibold text-[#C87400] mr-2">1.</span>
              <span>Check your email for a confirmation link</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold text-[#C87400] mr-2">2.</span>
              <span>Click to start your demo recording session</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold text-[#C87400] mr-2">3.</span>
              <span>Answer the question and see your transcription instantly</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold text-[#C87400] mr-2">4.</span>
              <span>Start your Memory Stream to preserve your stories forever</span>
            </li>
          </ol>
        </div>

        {/* Already Have Account */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/login')}
            className="text-[#061D3D] hover:text-[#C87400] font-medium text-sm transition-colors"
          >
            Already have an account? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
